/**
 * Offline-first action queue for caregiver clock in/out.
 *
 * Queues clock-in / clock-out POSTs in IndexedDB when offline (or any network
 * error) and replays them when the device regains connectivity.
 *
 * Public API:
 *   queueAction(type, visitId, payload)        → enqueue a pending action
 *   getQueue()                                  → list current queued actions
 *   removeAction(id)                            → remove a single queued item
 *   syncQueue(api)                              → drain & POST all queued items
 *   subscribe(listener)                         → fired whenever queue changes / sync events
 *   isOnline()                                  → boolean
 *
 * type values: "check-in" | "check-out"
 */

const DB_NAME = "caregrid-offline";
const STORE = "queue";
const DB_VERSION = 1;

let _dbPromise = null;
const _listeners = new Set();

function _emit(event) {
  for (const l of _listeners) {
    try { l(event); } catch (e) { /* noop */ }
  }
}

function _openDb() {
  if (!("indexedDB" in window)) return Promise.reject(new Error("No IndexedDB support"));
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "id", autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return _dbPromise;
}

async function _withStore(mode, fn) {
  const db = await _openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const store = tx.objectStore(STORE);
    const result = fn(store);
    tx.oncomplete = () => resolve(result?.value !== undefined ? result.value : result);
    tx.onerror = () => reject(tx.error);
  });
}

export const isOnline = () => (typeof navigator !== "undefined" ? navigator.onLine !== false : true);

export const subscribe = (listener) => {
  _listeners.add(listener);
  return () => _listeners.delete(listener);
};

export async function queueAction(type, visitId, payload) {
  const action = {
    type,
    visit_id: visitId,
    payload: payload || {},
    queued_at: new Date().toISOString(),
    attempts: 0,
    last_error: null,
  };
  const id = await new Promise((resolve, reject) => {
    _withStore("readwrite", (s) => {
      const r = s.add(action);
      r.onsuccess = () => resolve(r.result);
      r.onerror = () => reject(r.error);
    });
  });
  _emit({ kind: "queued", action: { ...action, id } });
  return { ...action, id };
}

export async function getQueue() {
  return new Promise((resolve, reject) => {
    _withStore("readonly", (s) => {
      const r = s.getAll();
      r.onsuccess = () => resolve(r.result || []);
      r.onerror = () => reject(r.error);
    });
  });
}

export async function removeAction(id) {
  return new Promise((resolve, reject) => {
    _withStore("readwrite", (s) => {
      const r = s.delete(id);
      r.onsuccess = () => resolve();
      r.onerror = () => reject(r.error);
    });
  });
}

async function _updateAction(id, updates) {
  return new Promise((resolve, reject) => {
    _withStore("readwrite", (s) => {
      const getReq = s.get(id);
      getReq.onsuccess = () => {
        const existing = getReq.result;
        if (!existing) return resolve();
        const merged = { ...existing, ...updates };
        const putReq = s.put(merged);
        putReq.onsuccess = () => resolve(merged);
        putReq.onerror = () => reject(putReq.error);
      };
      getReq.onerror = () => reject(getReq.error);
    });
  });
}

/**
 * Drain the queue. `api` must expose:
 *   api.checkIn(visitId, payload)
 *   api.checkOut(visitId, payload)
 * Returns { synced: n, failed: n, errors: [...] }
 */
export async function syncQueue(api) {
  if (!isOnline()) return { synced: 0, failed: 0, skipped: true };
  const items = await getQueue();
  if (items.length === 0) return { synced: 0, failed: 0 };
  _emit({ kind: "sync_start", count: items.length });

  let synced = 0;
  let failed = 0;
  const errors = [];
  // Process in queued_at order
  items.sort((a, b) => (a.queued_at < b.queued_at ? -1 : 1));
  for (const item of items) {
    try {
      if (item.type === "check-in") {
        await api.checkIn(item.visit_id, item.payload);
      } else if (item.type === "check-out") {
        await api.checkOut(item.visit_id, item.payload);
      } else {
        await removeAction(item.id);
        continue;
      }
      await removeAction(item.id);
      synced++;
      _emit({ kind: "synced", action: item });
    } catch (err) {
      failed++;
      const msg =
        err?.response?.data?.detail?.message ||
        (typeof err?.response?.data?.detail === "string" ? err.response.data.detail : null) ||
        err?.message ||
        "Unknown error during sync";
      errors.push({ action: item, error: msg });
      const status = err?.response?.status;
      const conflictDetail = err?.response?.data?.detail;
      const conflictKind = (conflictDetail && typeof conflictDetail === "object" && conflictDetail.conflict) || null;
      // 4xx (except 408/429): give up — visit may have been deleted, signature
      // already accepted, or duplicate. Keep so user can manually clear.
      // 5xx or network: leave queued and increment attempts.
      await _updateAction(item.id, {
        attempts: (item.attempts || 0) + 1,
        last_error: msg,
        last_status: status || null,
        conflict_kind: conflictKind, // "visit_deleted" | "reassigned" | "already_completed" | "already_checked_in" | null
        conflict_detail: conflictKind ? conflictDetail : null,
      });
    }
  }
  _emit({ kind: "sync_complete", synced, failed });
  return { synced, failed, errors };
}
